'use client';

import { useEffect } from 'react';
import { ReactComponent as Lock } from '@assets/icons/lock.svg';
import { ReactComponent as User } from '@assets/icons/user.svg';
import { login } from 'actions/auth';
import { ROUTES, signInSchema } from 'constant';
import { FormikValues, useFormik } from 'formik';
import { useSearchParams } from 'next/navigation';

import {
  Box,
  Checkbox,
  Flex,
  HStack,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setError } from 'store/reducers/userSlice';

import { getValueKey, hasNonEmptyValues } from 'utils';

import { AuthStepButtons } from 'components';
import { IconTextField, Link, Text } from 'ui';

const initialValues: FormikValues = {
  value: '',
  password: '',
  rememberMe: false
};

const LoginForm = () => {
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const {
    colors: { palette }
  } = useTheme();
  const { errorMsg } = useAppSelector((state) => state.user);

  const emailFromParams = searchParams.get('email') || '';

  const {
    values,
    errors,
    isValid,
    setFieldValue,
    setErrors,
    handleSubmit,
    getFieldProps
  } = useFormik({
    initialValues: emailFromParams
      ? {
          ...initialValues,
          value: emailFromParams
        }
      : initialValues,
    validationSchema: signInSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async ({ value, password, rememberMe }) => {
      const keyName = getValueKey(value);
      const params = {
        [keyName]: value,
        pass: password,
        keep_signed: rememberMe ? 1 : 0
      };
      await dispatch(login(params));
    }
  });

  const isButtonDisabled =
    !isValid || !hasNonEmptyValues(values, ['keep_signed']);
  const errorValue = (errorMsg || errors.value || errors.password) as string;
  const isErrorExist = errorValue || errorMsg;

  const handleClearErrors = () => {
    setErrors({});
    dispatch(setError(''));
  };

  const handleInputChange = (name: string) => (value: string) => {
    setFieldValue(name, value);

    if (isErrorExist) {
      handleClearErrors();
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => handleClearErrors(), []);

  return (
    <Box
      as='form'
      display='flex'
      w='100%'
      justifyContent='center'
      flexDirection='column'
      alignItems='center'
      onSubmit={handleSubmit}
    >
      <VStack spacing='10px' alignItems='center' w='100%'>
        <Flex w='full' gap='5px'>
          <IconTextField
            autoFocus
            w='full'
            {...getFieldProps('value')}
            placeholder='Username / Email'
            pb={{ base: 0, md: 1 }}
            leftIcon={
              <User
                fill={palette.auth.login.iconColor}
                width={22}
                height={27}
                opacity={0.7}
              />
            }
            iconBg={palette.auth.login.iconBg}
            onChange={handleInputChange('value')}
          />
          <Box
            display={{ base: 'none', md: 'block' }}
            alignContent='center'
            p='20px'
            bg={palette.auth.login.inputBg}
            minW='175px'
            borderStartEndRadius='6px'
          >
            <Checkbox
              colorScheme='primary'
              size='sm'
              isChecked={values.rememberMe}
              {...getFieldProps('rememberMe')}
            >
              <Text
                fontSize={14}
                color={palette.auth.login.text.primary}
                fontWeight='thirdly'
                letterSpacing={0.5}
                whiteSpace='nowrap'
              >
                Remember Me
              </Text>
            </Checkbox>
          </Box>
        </Flex>
        <Flex w='full' gap='5px'>
          <IconTextField
            {...getFieldProps('password')}
            pb={{ base: 0, md: 1 }}
            placeholder='Password'
            leftIcon={
              <Lock
                fill={palette.auth.login.iconColor}
                width={22}
                height={26}
                opacity={0.7}
              />
            }
            iconBg={palette.auth.login.iconBg}
            type='password'
            onChange={handleInputChange('password')}
          />
          <Box
            display={{ base: 'none', md: 'block' }}
            alignContent='center'
            p='20px'
            bg={palette.auth.login.inputBg}
            minW='175px'
            borderEndEndRadius='6px'
          >
            <Link to={ROUTES.forgotPassword}>
              <Text
                fontSize={14}
                lineHeight={1.3}
                letterSpacing={0.5}
                whiteSpace='nowrap'
                fontWeight='400'
              >
                Forgot Password?
              </Text>
            </Link>
          </Box>
        </Flex>

        <HStack
          w='100%'
          justifyContent='space-between'
          align='center'
          position='relative'
          flexWrap='wrap'
          minHeight='30px'
        >
          {isErrorExist ? (
            <Text
              w='100%'
              textAlign='center'
              color={palette.auth.login.text.error}
            >
              {isErrorExist}
            </Text>
          ) : (
            <Flex
              gap='5px'
              alignItems='center'
              w='100%'
              justifyContent='space-between'
              flexWrap='wrap'
              display={{ base: 'flex', md: 'none' }}
            >
              <Box>
                <Checkbox
                  colorScheme='primary'
                  size='sm'
                  isChecked={values.rememberMe}
                  {...getFieldProps('rememberMe')}
                >
                  <Text
                    fontSize={15}
                    color={palette.auth.login.text.primary}
                    fontWeight='thirdly'
                    letterSpacing={0.5}
                  >
                    Remember Me
                  </Text>
                </Checkbox>
              </Box>

              <Link to={ROUTES.forgotPassword}>
                <Text
                  fontSize={15}
                  fontWeight={400}
                  lineHeight={1.3}
                  letterSpacing={0.5}
                >
                  Forgot Password?
                </Text>
              </Link>
            </Flex>
          )}
        </HStack>

        <AuthStepButtons
          buttonWidth={175}
          rightLabel='Sign in'
          isBtnDisabled={isButtonDisabled}
        />
      </VStack>
    </Box>
  );
};

export default LoginForm;
